"use client";

import React from "react";
import { Box, CssBaseline, ThemeProvider } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { AuthProvider } from "context/AuthContext";
import AppShell from "components/AppShell";
import theme from "theme";
import { Provider } from "react-redux";
import { store } from "redux/store";
import { usePathname } from "next/navigation";

type ProvidersProps = {
  children: React.ReactNode;
};

const CHROMELESS_PATHS = new Set([
  "/accept-invite",
  "/access-denied",
  "/login",
  "/register",
  "/reset-password",
  "/verify-link",
]);
const AUTHLESS_PATHS = new Set(["/accept-invite"]);
const LIGHT_AUTH_PATHS = new Set([
  "/access-denied",
  "/login",
  "/register",
  "/reset-password",
  "/verify-link",
]);

function AppChrome({ children }: ProvidersProps) {
  const pathname = usePathname();

  // For the public invite acceptance page we must not render the AppShell or
  // expose any navigation elements even if the user already has a session.
  // This ensures invite links cannot be used to access the main UI menu.
  if (pathname && CHROMELESS_PATHS.has(pathname)) {
    return (
      <Box
        component="main"
        sx={{
          minHeight: "100vh",
          background:
            "radial-gradient(circle at top right, rgba(76,154,255,0.16), transparent 55%)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          p: { xs: 2, md: 6 },
        }}
      >
        {children}
      </Box>
    );
  }

  // Always render the AppShell so the top navigation and menu remain
  // visible even if the user's session expires. Certain pages (for
  // example `/accept-invite`) intentionally avoid mounting the
  // `AuthProvider` and `AppShell` above; those cases are handled above.
  // Rendering the AppShell while unauthenticated keeps the layout stable
  // and allows the app to show an explicit session-expired banner instead
  // of abruptly removing the navigation.

  return <AppShell>{children}</AppShell>;
}

export default function Providers({ children }: ProvidersProps) {
  const pathname = usePathname();

  // For the public invite acceptance page we intentionally do not mount the
  // AuthProvider so the user is not treated as an authenticated session even
  // if a session cookie exists. We still provide theme and redux store to the
  // page UI.
  if (pathname && AUTHLESS_PATHS.has(pathname)) {
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Provider store={store}>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <Box
              component="main"
              sx={{
                minHeight: "100vh",
                background:
                  "radial-gradient(circle at top right, rgba(76,154,255,0.16), transparent 55%)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                p: { xs: 2, md: 6 },
              }}
            >
              {children}
            </Box>
          </ThemeProvider>
        </Provider>
      </LocalizationProvider>
    );
  }

  if (pathname && LIGHT_AUTH_PATHS.has(pathname)) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <Box
            component="main"
            sx={{
              minHeight: "100vh",
              background:
                "radial-gradient(circle at top right, rgba(76,154,255,0.16), transparent 55%)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              p: { xs: 2, md: 6 },
            }}
          >
            {children}
          </Box>
        </AuthProvider>
      </ThemeProvider>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Provider store={store}>
        <AuthProvider>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <AppChrome>{children}</AppChrome>
          </ThemeProvider>
        </AuthProvider>
      </Provider>
    </LocalizationProvider>
  );
}
